import { For, Show } from "solid-js";
import { linesEntered } from "../../boundaries/user-list-input.parser";
import { BRIEF_READINESS_STATE, BRIEF_STATUS } from "../../enums/brief";
import { BriefSourceContext } from "./BriefSourceContext";
import {
  briefReadiness,
  canMarkBriefReady,
  isQuestionResolved,
} from "./briefReadiness";
import type { DeliveryBrief } from "./briefTypes";

export function BriefSourceRail(props: {
  brief: DeliveryBrief;
  dirty: boolean;
  onMarkReady: () => void;
  onReturnToDraft: () => void;
  onStartGovernance: () => void;
  onReset: () => void;
}) {
  const unresolved = () =>
    props.brief.openQuestions.filter(
      (question) => !isQuestionResolved(question),
    );

  return (
    <aside class="brief-source-rail" aria-label="Source and readiness">
      <section class="brief-readiness-panel">
        <span class="signal-mini-kicker">Input completeness</span>
        <h2>{props.brief.status}</h2>
        <p class="brief-readiness-note">
          These checks show that inputs are present. Review the proposed work
          before marking it ready for delivery.
        </p>
        <ul class="brief-readiness-list">
          <For each={briefReadiness(props.brief)}>
            {(item) => (
              <li class={`is-${item.state}`}>
                <span aria-hidden="true">
                  {item.state === BRIEF_READINESS_STATE.COMPLETE
                    ? "OK"
                    : item.state === BRIEF_READINESS_STATE.NOT_APPLICABLE
                      ? "N/A"
                      : "--"}
                </span>
                <div>
                  <strong>{item.label}</strong>
                  <small>{item.detail}</small>
                </div>
              </li>
            )}
          </For>
        </ul>
        <div class="brief-readiness-actions">
          <Show
            when={
              props.brief.status === BRIEF_STATUS.READY_FOR_DELIVERY &&
              canMarkBriefReady(props.brief)
            }
            fallback={
              <button
                type="button"
                class="brief-primary-action"
                disabled={!canMarkBriefReady(props.brief)}
                title={
                  canMarkBriefReady(props.brief)
                    ? "After review, mark this Preview brief ready for delivery"
                    : "Provide the required inputs and review the brief before marking ready"
                }
                onClick={props.onMarkReady}
              >
                Mark ready for delivery
              </button>
            }
          >
            <button
              type="button"
              class="brief-primary-action"
              onClick={props.onStartGovernance}
            >
              Start governance
            </button>
            <button type="button" onClick={props.onReturnToDraft}>
              Return to draft
            </button>
          </Show>
          <button type="button" disabled={!props.dirty} onClick={props.onReset}>
            Reset preview changes
          </button>
        </div>
      </section>

      <section class="brief-source-section is-inputs">
        <h3>
          {unresolved().length
            ? `${unresolved().length} input${unresolved().length === 1 ? "" : "s"} still required`
            : "All question responses recorded"}
        </h3>
        <Show
          when={unresolved().length > 0}
          fallback={
            <details>
              <summary>Recorded responses</summary>
              <ul>
                <For each={props.brief.openQuestions}>
                  {(question) => (
                    <li>
                      <strong>{question.text}</strong>
                      <p>{question.response}</p>
                    </li>
                  )}
                </For>
              </ul>
            </details>
          }
        >
          <div class="brief-blocker-list">
            <ul>
              <For each={unresolved()}>
                {(question) => (
                  <li>
                    <a href={`#brief-question-${question.id}`}>
                      {linesEntered(question.text)[0]}
                    </a>
                  </li>
                )}
              </For>
            </ul>
          </div>
        </Show>
      </section>
      <BriefSourceContext brief={props.brief} />
    </aside>
  );
}
